import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConnectionManageSurface from '@/components/connections/ConnectionManageSurface';
import { useLibraryIdentity } from '@/hooks/useLibraryIdentity';
import { useSubscription } from '@/hooks/useSubscription';
import { useLanguage } from '@/i18n/LanguageProvider';
import { supabase } from '@/integrations/supabase/client';
import { LIVE_REQUIRES_PREMIUM } from '@/lib/liveAccess';
import {
  invokeLanguageAction, languageOwnerSession, languageAuthSessionId, languageUuid, LANGUAGE_CODES,
  sameLanguageValue, type LanguageOwnerGuard, type LanguageSnapshot,
} from '@/lib/languageActionExecutor';
import { canonicalLanguageSnapshot, type LanguageCode, type LanguageProfileRecord } from '../../../supabase/functions/_shared/languageActionDomain';

export interface LanguageTutorHandoffProps {
  open: boolean;
  language: string;
  expected_user_id: string;
  expected_session_id?: string;
  action_id?: string;
  isCurrent: () => boolean;
  onClose: () => void;
  onStart?: (language: LanguageCode) => void;
}
const button = 'min-h-11 rounded-xl border border-border px-3 py-2 text-sm disabled:opacity-50';
const validLanguage = (value: string): value is LanguageCode => (LANGUAGE_CODES as readonly string[]).includes(value);
export function LanguageTutorHandoffDialog(props: LanguageTutorHandoffProps) {
  const identity = useLibraryIdentity(), { t } = useLanguage();
  if (!props.open || !props.isCurrent() || !identity.ready || !identity.userId || identity.userId !== props.expected_user_id || !languageUuid(props.expected_user_id)) return null;
  if (!validLanguage(props.language) || props.expected_session_id !== undefined && !languageUuid(props.expected_session_id)) {
    return <ConnectionManageSurface open onOpenChange={open => { if (!open) props.onClose(); }}
      title={t('languageClassAction.tutorTitle')} description={t('languageClassAction.tutorIntro')} closeLabel={t('common.close')} brand={<span aria-hidden="true">🌍</span>}>
      <p role="alert">{t('languageClassAction.tutorInvalidLanguage')}</p>
      <button type="button" className={button} onClick={props.onClose}>{t('common.close')}</button>
    </ConnectionManageSurface>;
  }
  return <LanguageTutorHandoffContent key={[identity.userId, identity.generation, props.expected_session_id ?? 'module', props.action_id ?? 'tutor', props.language].join(':')} {...props} language={props.language} />;
}
function LanguageTutorHandoffContent(props: LanguageTutorHandoffProps & { language: LanguageCode }) {
  const [origin] = useState(() => ({ owner: props.expected_user_id, session: props.expected_session_id, language: props.language }));
  const { t } = useLanguage(), navigate = useNavigate(), subscription = useSubscription();
  const mounted = useRef(false), epoch = useRef(0), authSession = useRef<string | null>(null), retired = useRef(false), lock = useRef(false);
  const currentOrigin = useRef(props.isCurrent); currentOrigin.current = props.isCurrent;
  const liveAccess = useRef(subscription); liveAccess.current = subscription;
  const [snapshot, setSnapshot] = useState<LanguageSnapshot | null>(null), [error, setError] = useState<string | null>(null), [busy, setBusy] = useState(false);
  const capture = useCallback((): LanguageOwnerGuard => {
    const run = epoch.current, session = authSession.current;
    const assert: LanguageOwnerGuard = () => {
      if (!mounted.current || retired.current || run !== epoch.current || !currentOrigin.current() || session && session !== authSession.current) throw new Error('invalid_session');
    };
    if (session) assert.authSessionId = session;
    return assert;
  }, []);
  const ownedOrigin = useCallback(async (assert: LanguageOwnerGuard) => {
    const session = await languageOwnerSession(origin.owner, assert); assert();
    const id = languageAuthSessionId(session.access_token);
    if (!id || authSession.current && id !== authSession.current) throw new Error('invalid_session');
    authSession.current = id; assert.authSessionId = id;
    if (origin.session) {
      const reply = await supabase.from('mirror_sessions').select('id,user_id,kind').eq('id', origin.session).eq('user_id', origin.owner).eq('kind', 'mirror').maybeSingle();
      assert();
      if (reply.error || !reply.data || reply.data.id !== origin.session || reply.data.user_id !== origin.owner || reply.data.kind !== 'mirror') throw new Error('invalid_session');
    }
  }, [origin.owner, origin.session]);
  const readProfile = useCallback(async (assert: LanguageOwnerGuard) => {
    await ownedOrigin(assert); assert();
    const reply = await invokeLanguageAction({ action: 'get', resource: 'profile', target: { language: origin.language } }, origin.owner, assert); assert();
    const result = canonicalLanguageSnapshot(reply.snapshot, origin.owner);
    if (!result.record || 'word' in result.record || result.target.language !== origin.language || result.record.learning_language !== origin.language) throw new Error('profile_missing');
    return result;
  }, [origin.language, origin.owner, ownedOrigin]);
  useEffect(() => {
    mounted.current = true; retired.current = false; const run = ++epoch.current;
    const retire = () => {
      retired.current = true; epoch.current++; authSession.current = null; lock.current = true;
      if (mounted.current) { setSnapshot(null); setBusy(false); setError('invalid_session'); }
    };
    const { data: { subscription: listener } } = supabase.auth.onAuthStateChange((_event, session) => {
      const id = languageAuthSessionId(session?.access_token);
      if (session?.user.id !== origin.owner || !id || authSession.current && authSession.current !== id) retire();
      else if (!retired.current) authSession.current = id;
    });
    const assert = capture();
    void (async () => {
      try { const fresh = await readProfile(assert); assert(); setSnapshot(fresh); }
      catch (cause) { if (mounted.current && run === epoch.current && !retired.current && currentOrigin.current()) setError(cause instanceof Error ? cause.message : 'read_failed'); }
    })();
    const lifecycle = epoch;
    return () => { mounted.current = false; lifecycle.current++; listener.unsubscribe(); };
  }, [origin.owner, capture, readProfile]);
  const start = async () => {
    if (!snapshot || busy || lock.current || retired.current || liveAccess.current.isLoading || LIVE_REQUIRES_PREMIUM && !liveAccess.current.isPremium) return;
    const assert = capture(), reviewed = structuredClone(snapshot); lock.current = true; setBusy(true); setError(null);
    try {
      const fresh = await readProfile(assert); assert();
      if (!sameLanguageValue(reviewed, fresh)) { setSnapshot(fresh); throw new Error('profile_changed'); }
      if (liveAccess.current.isLoading || LIVE_REQUIRES_PREMIUM && !liveAccess.current.isPremium) throw new Error('premium_required');
      assert();
      if (props.onStart) props.onStart(origin.language);
      else navigate('/language/live?lang=' + origin.language);
      props.onClose();
    } catch (cause) { try { assert(); setError(cause instanceof Error ? cause.message : 'read_failed'); } catch { /* retired actor */ } }
    finally { try { assert(); lock.current = false; setBusy(false); } catch { /* retired actor */ } }
  };
  const profile = snapshot?.record as LanguageProfileRecord | null;
  const premiumBlocked = !subscription.isLoading && LIVE_REQUIRES_PREMIUM && !subscription.isPremium;
  const errorKey = error === 'invalid_session' ? 'tutorAccountChanged' : error === 'profile_missing' || error === 'ambiguous_target' ? 'tutorProfileMissing'
    : error === 'profile_changed' ? 'tutorProfileChanged' : error === 'premium_required' ? 'tutorPremiumRequired' : error ? 'tutorReadError' : null;
  return <ConnectionManageSurface open onOpenChange={open => { if (!open) props.onClose(); }}
    title={t('languageClassAction.tutorTitle')} description={t('languageClassAction.tutorIntro')} closeLabel={t('common.close')} brand={<span aria-hidden="true">🌍</span>}>
    <div className="min-w-0 space-y-4 p-1">
      {errorKey && <p role="alert">{t('languageClassAction.' + errorKey)}</p>}
      {!snapshot && !errorKey && <p role="status">{t('common.loading')}</p>}
      {profile && <section className="space-y-2 rounded-xl border border-border p-3">
        <p>{t('languageAction.field.language')}: {t('languageAction.language.' + profile.learning_language)}</p>
        <p>{t('languageAction.field.native_language')}: {t('languageAction.language.' + profile.native_language)}</p>
        <p>{t('languageAction.field.level')}: {t('languageAction.level.' + profile.level)}</p>
        <p>{t('languageAction.field.daily_goal_minutes')}: {profile.daily_goal_minutes} {t('common.min')}</p>
      </section>}
      {premiumBlocked && <p role="alert">{t('languageClassAction.tutorPremiumRequired')}</p>}
      <div className="flex flex-wrap gap-3">
        <button type="button" className={button + ' bg-primary text-primary-foreground font-semibold'} disabled={!profile || busy || subscription.isLoading || premiumBlocked || retired.current} onClick={() => void start()}>{t(busy ? 'common.loading' : 'languageClassAction.tutorStart')}</button>
        <button type="button" className={button} onClick={props.onClose}>{t('common.close')}</button>
      </div>
    </div>
  </ConnectionManageSurface>;
}
export default LanguageTutorHandoffDialog;
